import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { openCookiePreferencesModal } from "@/lib/cookieConsent";

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-3 relative flex items-center justify-center">
          <Button variant="ghost" size="icon" className="shrink-0 absolute left-4" asChild>
            <Link to="/">
              <ArrowLeft className="w-5 h-5" />
            </Link>
          </Button>
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <img
              src="/dibs_assets/red_logo_with_transparent_bg.png"
              alt="Dibs"
              className="h-7 object-contain"
            />
            <span className="text-primary font-sans font-bold text-xl tracking-tight">Dibs</span>
          </Link>
        </div>
      </div>

      {/* Content */}
      <article className="max-w-4xl mx-auto px-4 py-12 sm:py-16">
        <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight mb-2">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mb-12">Last updated: August 14, 2026</p>

        <div className="prose prose-neutral dark:prose-invert max-w-none space-y-10 text-[15px] leading-relaxed">

          {/* 1 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">1. Introduction</h2>
            <p className="text-muted-foreground">
              Welcome to <strong className="text-foreground">Dibs</strong> ("we," "us," or "our"). Dibs is a dynamic pricing booking system designed for service-based businesses, operated from Singapore.
            </p>
            <p className="text-muted-foreground mt-3">
              This Privacy Policy explains how we collect, use, disclose, and protect your personal data when you use our website, platform, and services (collectively, the "Service"). By accessing or using the Service, you agree to the practices described in this Privacy Policy.
            </p>
            <div className="mt-4 rounded-lg border border-primary/20 bg-primary/5 p-4">
              <p className="text-sm text-foreground font-medium mb-1">This policy applies to two categories of users:</p>
              <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                <li><strong className="text-foreground">Business Users</strong> — business owners and administrators who sign up for a Dibs account to manage bookings, listings, and pricing.</li>
                <li><strong className="text-foreground">End-User Customers</strong> — individuals who make bookings through a Dibs-powered booking page.</li>
              </ul>
            </div>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">2. Information We Collect</h2>

            <h3 className="text-lg font-medium mt-6 mb-3">2.1 Business User Data</h3>
            <p className="text-muted-foreground mb-3">When you create an account and onboard your business, we collect:</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-4 font-medium text-foreground">Data Type</th>
                    <th className="text-left py-2 font-medium text-foreground">Examples</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-b border-dashed">
                    <td className="py-2 pr-4 font-medium text-foreground/80">Account credentials</td>
                    <td className="py-2">Email address, password (hashed), Google account profile (if using Google Sign-In)</td>
                  </tr>
                  <tr className="border-b border-dashed">
                    <td className="py-2 pr-4 font-medium text-foreground/80">Business profile</td>
                    <td className="py-2">Business name, business email, phone number, business address, business category/description</td>
                  </tr>
                  <tr className="border-b border-dashed">
                    <td className="py-2 pr-4 font-medium text-foreground/80">Social media links</td>
                    <td className="py-2">Instagram URL, Facebook URL, Twitter/X URL</td>
                  </tr>
                  <tr className="border-b border-dashed">
                    <td className="py-2 pr-4 font-medium text-foreground/80">Subscription & billing</td>
                    <td className="py-2">Selected plan, billing cycle, Stripe Customer ID, Stripe Connect ID</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-medium text-foreground/80">Listing information</td>
                    <td className="py-2">Service names, descriptions, pricing, capacity, schedules, images, terms & conditions</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-medium mt-8 mb-3">2.2 End-User Customer Data</h3>
            <p className="text-muted-foreground mb-3">When a customer makes a booking through a Dibs-powered page, we collect:</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-4 font-medium text-foreground">Data Type</th>
                    <th className="text-left py-2 font-medium text-foreground">Required?</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-b border-dashed">
                    <td className="py-2 pr-4">Full name</td>
                    <td className="py-2">Required</td>
                  </tr>
                  <tr className="border-b border-dashed">
                    <td className="py-2 pr-4">Email address</td>
                    <td className="py-2">Required</td>
                  </tr>
                  <tr className="border-b border-dashed">
                    <td className="py-2 pr-4">Phone number</td>
                    <td className="py-2">Optional</td>
                  </tr>
                  <tr className="border-b border-dashed">
                    <td className="py-2 pr-4">Booking details</td>
                    <td className="py-2">Date, time, number of pax, listing selected</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Payment information</td>
                    <td className="py-2">Payment method selection; card details are processed directly by Stripe and are <strong className="text-foreground">never</strong> stored on our servers</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-medium mt-8 mb-3">2.3 Automatically Collected Data</h3>
            <p className="text-muted-foreground">When you access the Service, we may automatically collect:</p>
            <ul className="text-muted-foreground mt-2 ml-4 list-disc space-y-1">
              <li>Device & browser information — browser type, operating system, screen resolution</li>
              <li>Usage data — pages visited, features used, timestamps</li>
              <li>Log data — IP address, access times, referring URLs</li>
              <li>Local storage data — session tokens, authentication state, and temporary onboarding progress</li>
            </ul>

            <h3 className="text-lg font-medium mt-8 mb-3">2.4 AI and Connected-Channel Data</h3>
            <p className="text-muted-foreground">
              If a business enables Dibs AI or connects a messaging, form, CRM, or data service, we process the conversation content, attachments, customer context, tool requests, and delivery metadata needed to provide that feature. Relevant content may be sent to the selected AI or integration provider. Provider credentials are stored in restricted server-side storage and are not exposed to customer browsers.
            </p>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">3. How We Use Your Information</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-4 font-medium text-foreground">Purpose</th>
                    <th className="text-left py-2 font-medium text-foreground">Legal Basis</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-b border-dashed">
                    <td className="py-2 pr-4"><strong className="text-foreground/80">Providing the Service</strong> — managing accounts, processing bookings, displaying listing pages</td>
                    <td className="py-2">Performance of contract</td>
                  </tr>
                  <tr className="border-b border-dashed">
                    <td className="py-2 pr-4"><strong className="text-foreground/80">Payment processing</strong> — facilitating payments via Stripe, managing subscriptions and payouts</td>
                    <td className="py-2">Performance of contract</td>
                  </tr>
                  <tr className="border-b border-dashed">
                    <td className="py-2 pr-4"><strong className="text-foreground/80">Communications</strong> — sending booking confirmations, modifications, cancellation notices, and payout summaries</td>
                    <td className="py-2">Performance of contract / Legitimate interest</td>
                  </tr>
                  <tr className="border-b border-dashed">
                    <td className="py-2 pr-4"><strong className="text-foreground/80">Analytics & insights</strong> — providing booking analytics, revenue data, and occupancy metrics</td>
                    <td className="py-2">Legitimate interest</td>
                  </tr>
                  <tr className="border-b border-dashed">
                    <td className="py-2 pr-4"><strong className="text-foreground/80">AI-assisted communications</strong> — generating or classifying replies, summaries, workflow drafts, and business insights when the feature is enabled</td>
                    <td className="py-2">Performance of contract / Legitimate interest</td>
                  </tr>
                  <tr className="border-b border-dashed">
                    <td className="py-2 pr-4"><strong className="text-foreground/80">Security</strong> — protecting accounts, verifying emails, password recovery</td>
                    <td className="py-2">Legitimate interest</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4"><strong className="text-foreground/80">Legal compliance</strong> — complying with applicable laws and regulations</td>
                    <td className="py-2">Legal obligation</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">4. Third-Party Services</h2>
            <p className="text-muted-foreground mb-4">We use the following third-party services to operate the platform. Each has its own privacy policy governing how they handle your data:</p>

            <div className="space-y-4">
              {[
                {
                  name: "Supabase",
                  purpose: "Database hosting, user authentication, file storage, and serverless functions. All data is stored in Singapore (ap-southeast-1) with Row Level Security enabled.",
                  url: "https://supabase.com/privacy",
                },
                {
                  name: "Stripe",
                  purpose: "Payment processing (Stripe Checkout), subscription billing, business payouts via Stripe Connect, and refunds. Card details are processed directly by Stripe and never touch our servers.",
                  url: "https://stripe.com/privacy",
                },
                {
                  name: "Google (OAuth)",
                  purpose: 'Optional "Sign in with Google" authentication for business users. We receive your Google email, name, and profile picture.',
                  url: "https://policies.google.com/privacy",
                },
                {
                  name: "Resend",
                  purpose: "Transactional email delivery — booking confirmations, modifications, cancellations, welcome emails, and payout summaries.",
                  url: "https://resend.com/legal/privacy-policy",
                },
                {
                  name: "Vercel",
                  purpose: "Web application hosting and content delivery.",
                  url: "https://vercel.com/legal/privacy-policy",
                },
                {
                  name: "Google Gemini",
                  purpose: "AI model processing for enabled conversational, drafting, classification, and insight features. Requests can include relevant business instructions and customer conversation context.",
                  url: "https://policies.google.com/privacy",
                },
                {
                  name: "OpenAI",
                  purpose: "AI model processing for selected drafting, classification, and fallback routes when enabled.",
                  url: "https://openai.com/policies/privacy-policy/",
                },
                {
                  name: "Twilio",
                  purpose: "Optional WhatsApp delivery and business subaccounts. Twilio processes recipient identifiers, message content, media and delivery events.",
                  url: "https://www.twilio.com/en-us/legal/privacy",
                },
                {
                  name: "Meta",
                  purpose: "Optional WhatsApp Cloud, Instagram, and Facebook Messenger channels. Meta processes platform identifiers, message content, attachments, and delivery events.",
                  url: "https://www.facebook.com/privacy/policy/",
                },
                {
                  name: "Telegram",
                  purpose: "Optional bot and Business Connection messaging. Telegram processes platform identifiers, message content, attachments, and connection metadata.",
                  url: "https://telegram.org/privacy",
                },
                {
                  name: "LINE",
                  purpose: "Optional LINE messaging. LINE processes platform identifiers, message content, attachments, and delivery events.",
                  url: "https://terms.line.me/line_rules/",
                },
                {
                  name: "Inngest",
                  purpose: "Workflow orchestration and delivery of scheduled or event-driven jobs. Workflow events are limited to the fields needed to run the job.",
                  url: "https://www.inngest.com/privacy",
                },
                {
                  name: "Sentry",
                  purpose: "Application error diagnostics. Default personal-data collection and session replay are disabled; diagnostic payload fields are stripped before sending.",
                  url: "https://sentry.io/privacy/",
                },
                {
                  name: "Tally",
                  purpose: "Optional form and CRM ingestion selected by a business. Dibs exchanges the forms, submissions, mapped records, and credentials needed for that connection.",
                  url: "https://tally.so/help/privacy-policy",
                },
                {
                  name: "Mindbody and owner-configured integrations",
                  purpose: "Optional CRM and external-data connections selected by a business. Dibs exchanges only the records and credentials needed for the requested integration.",
                  url: "https://co.mindbodyonline.com/legal/privacy-policy",
                },
              ].map((service) => (
                <div key={service.name} className="rounded-lg border p-4">
                  <h4 className="font-medium text-foreground">{service.name}</h4>
                  <p className="text-sm text-muted-foreground mt-1">{service.purpose}</p>
                  <a
                    href={service.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline mt-2 inline-block"
                  >
                    View Privacy Policy →
                  </a>
                </div>
              ))}
            </div>
          </section>

          {/* 5 */}
          <section id="cookies">
            <h2 className="text-xl font-semibold mb-4">5. Cookies and Local Storage</h2>
            <p className="text-muted-foreground mb-3">
              Dibs uses <strong className="text-foreground">first-party cookies</strong> and <strong className="text-foreground">browser local storage</strong> grouped into three categories. Optional categories remain off by default until you opt in:
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-4 font-medium text-foreground">Item</th>
                    <th className="text-left py-2 pr-4 font-medium text-foreground">Category</th>
                    <th className="text-left py-2 pr-4 font-medium text-foreground">Purpose</th>
                    <th className="text-left py-2 font-medium text-foreground">Duration</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-b border-dashed">
                    <td className="py-2 pr-4">Authentication session (<code className="text-xs">localStorage</code>)</td>
                    <td className="py-2 pr-4">Strictly Necessary</td>
                    <td className="py-2 pr-4">Maintaining your login state, session token, and active business/outlet context</td>
                    <td className="py-2">Until logout or session expiry</td>
                  </tr>
                  <tr className="border-b border-dashed">
                    <td className="py-2 pr-4">Onboarding &amp; OAuth state (<code className="text-xs">localStorage</code>)</td>
                    <td className="py-2 pr-4">Strictly Necessary</td>
                    <td className="py-2 pr-4">Saving onboarding progress and verifying OAuth / Stripe setup flows</td>
                    <td className="py-2">Until flow completion</td>
                  </tr>
                  <tr className="border-b border-dashed">
                    <td className="py-2 pr-4">Cookie consent (<code className="text-xs">dibs_cookie_consent</code>)</td>
                    <td className="py-2 pr-4">Strictly Necessary</td>
                    <td className="py-2 pr-4">Recording your cookie and storage consent preferences across Dibs subdomains</td>
                    <td className="py-2">12 months</td>
                  </tr>
                  <tr className="border-b border-dashed">
                    <td className="py-2 pr-4">Sidebar &amp; UI layout (<code className="text-xs">sidebar:state</code>)</td>
                    <td className="py-2 pr-4">Functional Preferences (Opt-in)</td>
                    <td className="py-2 pr-4">Remembering whether the admin sidebar is expanded or collapsed</td>
                    <td className="py-2">7 days</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Error &amp; performance diagnostics (Sentry)</td>
                    <td className="py-2 pr-4">Analytics &amp; Diagnostics (Opt-in)</td>
                    <td className="py-2 pr-4">Detecting application crashes and latency with personal payloads stripped</td>
                    <td className="py-2">Session only (when enabled)</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-muted-foreground mt-4">We do <strong className="text-foreground">not</strong> use advertising or cross-site tracking cookies. We do <strong className="text-foreground">not</strong> sell your data to third parties.</p>
            <div className="mt-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={openCookiePreferencesModal}
                data-testid="privacy-policy-cookie-preferences"
              >
                Manage Cookie Preferences
              </Button>
            </div>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">6. Data Sharing and Disclosure</h2>
            <p className="text-muted-foreground mb-3">We share your personal data only in the following circumstances:</p>
            <ul className="text-muted-foreground ml-4 list-disc space-y-2">
              <li><strong className="text-foreground">Business Users ↔ Their Customers:</strong> When a customer makes a booking, their details are made available to the business they are booking with, and vice versa.</li>
              <li><strong className="text-foreground">Payment processors:</strong> Transaction data is shared with Stripe to process payments and manage payouts.</li>
              <li><strong className="text-foreground">Email delivery:</strong> Recipient email addresses and email content are shared with Resend for transactional emails.</li>
              <li><strong className="text-foreground">AI and connected services:</strong> When a business enables an AI, messaging, workflow, form, CRM, or external-data feature, the data needed to perform that request is shared with the applicable provider.</li>
              <li><strong className="text-foreground">Legal requirements:</strong> We may disclose data if required by law, court order, or governmental authority.</li>
              <li><strong className="text-foreground">Business transfers:</strong> In the event of a merger, acquisition, or sale of assets, user data may be transferred as part of the transaction.</li>
            </ul>
            <p className="text-muted-foreground mt-4">We do <strong className="text-foreground">not</strong> sell, rent, or trade your personal information to third parties for marketing purposes.</p>
          </section>

          {/* 7 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">7. Data Retention</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-4 font-medium text-foreground">Data Type</th>
                    <th className="text-left py-2 font-medium text-foreground">Retention Period</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-b border-dashed">
                    <td className="py-2 pr-4">Business account data</td>
                    <td className="py-2">Retained while the account is active; deleted upon termination (subject to legal obligations)</td>
                  </tr>
                  <tr className="border-b border-dashed">
                    <td className="py-2 pr-4">Customer booking data</td>
                    <td className="py-2">Retained while the associated business account is active</td>
                  </tr>
                  <tr className="border-b border-dashed">
                    <td className="py-2 pr-4">Transaction records</td>
                    <td className="py-2">Retained as required by applicable financial and tax regulations</td>
                  </tr>
                  <tr className="border-b border-dashed">
                    <td className="py-2 pr-4">Application and security logs</td>
                    <td className="py-2">Retained for a limited operational and security period; sensitive fields are redacted or omitted where practical</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Onboarding temporary data</td>
                    <td className="py-2">Deleted from local storage upon completion</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* 8 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">8. Data Security</h2>
            <ul className="text-muted-foreground ml-4 list-disc space-y-2">
              <li><strong className="text-foreground">Encryption in transit:</strong> All data is transmitted over HTTPS/TLS.</li>
              <li><strong className="text-foreground">Database security:</strong> Tenant authorization controls, including Row Level Security (RLS) and restricted server-side service boundaries, limit access to business data.</li>
              <li><strong className="text-foreground">Password security:</strong> Passwords are hashed using industry-standard algorithms; we never store plaintext passwords.</li>
              <li><strong className="text-foreground">Payment security:</strong> Credit card details are processed directly by Stripe (PCI-DSS compliant) and never touch our servers.</li>
              <li><strong className="text-foreground">Access controls:</strong> Admin routes are protected by role-based access controls.</li>
            </ul>
          </section>

          {/* 9 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">9. Your Rights</h2>

            <h3 className="text-lg font-medium mt-6 mb-3">For All Users</h3>
            <ul className="text-muted-foreground ml-4 list-disc space-y-1">
              <li><strong className="text-foreground">Access:</strong> Request a copy of the personal data we hold about you.</li>
              <li><strong className="text-foreground">Correction:</strong> Request correction of inaccurate or incomplete data.</li>
              <li><strong className="text-foreground">Deletion:</strong> Request deletion of your personal data (subject to legal retention requirements).</li>
              <li><strong className="text-foreground">Portability:</strong> Request your data in a structured, machine-readable format.</li>
            </ul>

            <h3 className="text-lg font-medium mt-6 mb-3">For EU/EEA Residents (GDPR)</h3>
            <p className="text-muted-foreground">You additionally have the right to restrict processing, object to processing based on legitimate interests, withdraw consent at any time, and lodge a complaint with your local data protection authority.</p>

            <h3 className="text-lg font-medium mt-6 mb-3">For Singapore Residents (PDPA)</h3>
            <p className="text-muted-foreground">Under the Personal Data Protection Act 2012 (PDPA), you have the right to access and correct your personal data, withdraw consent, and request information about how your data has been used or disclosed in the past year.</p>
          </section>

          {/* 10 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">10. International Data Transfers</h2>
            <p className="text-muted-foreground">Your data is primarily stored in <strong className="text-foreground">Singapore (ap-southeast-1)</strong>. As we use global third-party services, your data may be transferred to and processed in other countries. Where such transfers occur, we ensure appropriate safeguards are in place as required by applicable data protection laws.</p>
          </section>

          {/* 11 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">11. Children's Privacy</h2>
            <p className="text-muted-foreground">The Service is not intended for use by individuals under the age of 18. We do not knowingly collect personal data from children. If you believe we have collected data from a child, please contact us immediately.</p>
          </section>

          {/* 12 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">12. Changes to This Privacy Policy</h2>
            <p className="text-muted-foreground">We may update this Privacy Policy from time to time. When we make material changes, we will update the "Last updated" date at the top of this page and notify business users via email or through the admin dashboard.</p>
          </section>

          {/* 13 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">13. Contact Us</h2>
            <p className="text-muted-foreground mb-4">If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:</p>
            <div className="rounded-lg border p-5">
              <p className="font-semibold text-foreground">Dibs Everything Pte Ltd</p>
              <p className="text-sm text-muted-foreground mt-1">📧 hello@usedibs.com</p>
              <p className="text-sm text-muted-foreground">📍 Singapore</p>
            </div>
          </section>
        </div>

        <p className="text-xs text-muted-foreground/70 italic mt-12 text-center">This policy is general information and not legal advice.</p>
      </article>
    </div>
  );
};

export default PrivacyPolicy;
